import { useLocation } from 'react-router';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §2] A PICK IN THE TREE MOVES FOCUS TO THE PANE IT OPENED — and nothing else does.
 *
 *  A tree link carries `LIBRARY_PICK` as its history state; the pane's heading takes focus when it
 *  mounts under that state, which also scrolls it into view — the pane may be below the fold, or
 *  under the tree's bar on a narrow screen. Opening the library from a link, a bookmark or a reload
 *  moves nothing: focus is the person's until they pick.
 *
 *  ⚠️ THE REF IS A MODULE-LEVEL FUNCTION, so it is called once per heading MOUNT and never on a
 *  re-render; a pane keys its heading on what it shows, so the next pick is a new mount.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const LIBRARY_PICK = { libraryPick: true } as const;

const focusHeading = (node: HTMLHeadingElement | null): void => {
  node?.focus();
};

const isPick = (state: unknown): boolean =>
  typeof state === 'object' && state !== null && 'libraryPick' in state && state.libraryPick === true;

/** The pane heading's ref — `undefined` unless this pane was opened from the tree. */
export const usePaneHeadingRef = (): ((node: HTMLHeadingElement | null) => void) | undefined => {
  const location = useLocation();
  return isPick(location.state) ? focusHeading : undefined;
};
